/**
 * QUIPWA — useDecks
 *
 * React Query hooks for deck data from StateDBService.
 * All deck-related data access in the UI goes through these hooks.
 */

import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDB } from './useDB.tsx'
import { evictCollectionCache } from '../lib/db/collection-db-service.ts'
import { OUTBOX } from '../lib/app-services.ts'
import type { DeckInstance, DeckStats, DeckWithStats } from '../types/domain.ts'


// ─── QUERY KEYS ──────────────────────────────────────────────────────────────

export const DECK_KEYS = {
  all:        ['decks'] as const,
  withStats:  ['decks', 'withStats'] as const,
  instance:   (id: string) => ['decks', id] as const,
  stats:      (id: string) => ['decks', id, 'stats'] as const,
}

// ─── HOOKS ───────────────────────────────────────────────────────────────────

/** All deck instances with their pre-aggregated stats */
export function useDecks() {
  const { service, isReady } = useDB()

  return useQuery({
    queryKey: DECK_KEYS.withStats,
    queryFn: async (): Promise<DeckWithStats[]> => {
      if (!service) return []
      const raw = await service.getDeckInstancesWithStats(); return raw.map(r => ({ instance: r, stats: r.stats }))
    },
    enabled: isReady,
    staleTime: 10_000,
  })
}

/** Single deck instance */
export function useDeck(id: string) {
  const { service, isReady } = useDB()

  return useQuery({
    queryKey: DECK_KEYS.instance(id),
    queryFn: async (): Promise<DeckInstance | null> => service?.getDeckInstance(id) ?? null,
    enabled: isReady && id.length > 0,
  })
}

/** Deck stats for a specific instance (with real avg_retention) */
export function useDeckStats(deckInstanceId: string) {
  const { service, isReady } = useDB()

  return useQuery({
    queryKey: DECK_KEYS.stats(deckInstanceId),
    queryFn: async (): Promise<DeckStats | null> =>
      service?.getDeckStatsWithRetention(deckInstanceId) ?? null,
    enabled: isReady && deckInstanceId.length > 0,
    staleTime: 5_000,
  })
}

/** Invalidate all deck queries (call after import / session complete) */
export function useInvalidateDecks() {
  const qc = useQueryClient()
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: DECK_KEYS.all })
  }, [qc])
}

/**
 * Delete a deck — locally first, works offline.
 *
 * The deck and its .wflash get tombstones in the replicated state, so no
 * device brings it back and discovery never re-imports the file (that used to
 * be why a delete had to refuse when Drive was unreachable). Moving the file
 * to Drive's trash is queued in the outbox and happens on the next sync.
 */
export function useDeleteDeck() {
  const { service, services, sync } = useDB()
  const invalidate = useInvalidateDecks()

  return useMutation({
    mutationFn: async (deckInstanceId: string) => {
      if (!service || !services) throw new Error('DB not ready')
      const instance = await service.getDeckInstance(deckInstanceId)

      await service.deleteDeckInstance(deckInstanceId)
      await evictCollectionCache(deckInstanceId)
      // A queued upload of this deck would otherwise re-create its file.
      await services.deckUploads.forget(deckInstanceId)
      if (instance?.drive_file_id) {
        await services.outbox.enqueue(OUTBOX.driveTrash, instance.drive_file_id, { fileId: instance.drive_file_id })
      }
      sync?.requestSync('deck-deleted', { urgent: true })
    },
    onSuccess: invalidate,
  })
}

/** Update visual styles for a deck */
export function useUpdateDeckVisuals() {
  const { service } = useDB()
  const invalidate = useInvalidateDecks()

  return useMutation({
    mutationFn: async ({ id, color, style }: { id: string; color: string | null; style: string | null }) => {
      if (!service) throw new Error('DB not ready')
      await service.updateDeckVisuals(id, color, style)
    },
    onSuccess: invalidate,
  })
}
